<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3 pb-4">
      <SimplePageHeader title="New Import" class="px-4 pr-5 mb-3" />
      <div class="px-4">
        <v-row>
          <v-col cols="12" sm="6" md="4">
            <ClientsDropDownList
              :id="clientId"
              isRequired
              dense
              @clientIdChange="clientId = $event"
            />
          </v-col>
        </v-row>

        <div class="subtitle-2 mt-2 mb-1">Download a template</div>
        <v-form>
        <v-row>
          <v-col cols="12" sm="6" md="4">
            <SimpleDatePicker
              label="Start Date *"
              :value="startDate"
              :isRequired="true"
              dense
              @selected-date-change="startDate = $event"
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <SimpleDatePicker
              label="End Date *"
              :value="endDate"
              :isRequired="true"
              dense
              @selected-date-change="endDate = $event"
            />
          </v-col>
          <v-col cols="12" sm="6" md="4" class="d-flex align-center">
            <v-btn color="primary" outlined :disabled="!canDownloadTemplate" @click="handleDownloadTemplate">
              <v-icon left>cloud_download</v-icon>Download Template
            </v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="pt-0">
            <div class="caption grey--text">
              One row per assignment per day over the range, with identifiers already filled in and hour columns blank.
              Fill in only the hours you want to change: a blank cell is left untouched, and only typed values are
              written. Type 0 to clear hours already entered for that day.
            </div>
          </v-col>
        </v-row>
        </v-form>

        <div class="subtitle-2 mt-3 mb-1">Upload a file</div>
        <v-form ref="uploadForm">
        <v-row v-if="formats.length">
          <v-col cols="12" sm="6" md="4">
            <v-select
              v-model="formatKey"
              :items="formatOptions"
              label="Format"
              outlined
              dense
              hide-details
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="4">
            <v-file-input
              v-model="file"
              label="File *"
              accept=".csv,.xlsx"
              outlined
              dense
              show-size
              prepend-icon="upload_file"
              :rules="[v => !!v || 'A file is required']"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="d-flex align-center">
            <v-btn color="primary" :disabled="!canUpload" @click="handleUpload">Upload</v-btn>
            <v-btn class="ml-2" text @click="goToList">Cancel</v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <div class="caption grey--text">
              The file is validated first and nothing is written. You review what will be imported and
              what failed, then Import when it looks right. A .csv or .xlsx in the Standard Import Format is accepted.
            </div>
          </v-col>
        </v-row>
        </v-form>
      </div>
    </div>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { BottomSpacer } from '@/components/buttons'
import { SimplePageHeader } from '@/components/pageComponents'
import { ClientsDropDownList } from '@/components/clients'
import { SimpleDatePicker } from '@/components/shared/datePickers'
import { importsRoute, importRunDetailRoute } from '@/lib/constants/routes'

export default {
  components: {
    BottomSpacer,
    SimplePageHeader,
    ClientsDropDownList,
    SimpleDatePicker
  },
  data () {
    return {
      clientId: null,
      file: null,
      startDate: null,
      endDate: null,
      formats: [],
      formatKey: null
    }
  },
  computed: {
    canUpload () {
      return !!this.clientId && !!this.file
    },
    canDownloadTemplate () {
      return !!this.clientId && !!this.startDate && !!this.endDate
    },
    // The standard upload always leads, then each Custom Import Format the Client may send. A null
    // value is the standard upload -- the same file the template produces -- so nobody has to pick.
    formatOptions () {
      return [{ value: null, text: 'Standard upload' }]
        .concat(this.formats.map(format => ({ value: format.formatKey, text: format.displayName })))
    }
  },
  watch: {
    clientId (clientId) {
      // A different Client has a different set of formats, so the previous Client's choice cannot
      // carry over: reset to the standard upload and load the new Client's formats.
      this.formats = []
      this.formatKey = null
      if (clientId) {
        this.loadFormats(clientId)
      }
    }
  },
  methods: {
    ...mapActions('importUpload', ['upload', 'downloadTemplate', 'fetchFormats']),
    goToList () {
      this.$router.push({ name: importsRoute.name })
    },
    loadFormats (clientId) {
      this.fetchFormats({ clientId })
        .then(formats => {
          // Guard against a slow response landing after the admin has moved to another Client.
          if (clientId === this.clientId) {
            this.formats = formats
          }
        })
        .catch(() => {})
    },
    handleDownloadTemplate () {
      this.downloadTemplate({ clientId: this.clientId, startDate: this.startDate, endDate: this.endDate })
        .catch(() => {})
    },
    handleUpload () {
      if (!this.$refs.uploadForm.validate()) return

      this.upload({ clientId: this.clientId, file: this.file, formatKey: this.formatKey })
        .then(run => {
          if (!run) return
          this.$router.push({ name: importRunDetailRoute.name, params: { id: run.id } })
        })
        .catch(() => {})
    }
  }
}
</script>
